<!-- ClientFeedback.vue -->
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import {
  EditIcon,
  EyeIcon,
  RefreshIcon
} from '@/icons'
import CommentIcon from '@/icons/CommentIcon.vue'
import EmptyState from '@/components/common/EmptyState.vue'

// Types
interface FeedbackItem {
  id: string
  srNo: number
  tenderId: string
  tenderName: string
  clientId: string
  clientName: string
  clientEmail: string
  clientPhone: string
  itemType: 'laptop' | 'printer' | 'server' | 'network' | 'software' | 'other'
  itemDescription: string
  itemBrand?: string
  itemModel?: string
  feedbackDate: string
  feedbackType: 'complaint' | 'suggestion' | 'query' | 'appreciation'
  feedbackPriority: 'low' | 'medium' | 'high' | 'critical'
  feedbackMessage: string
  status: 'new' | 'assigned_to_oem' | 'in_progress' | 'resolved' | 'closed' | 'reopened'
  assignedToAgentId?: string
  assignedToAgentName?: string
  assignedToEngineerId?: string
  assignedToEngineerName?: string
  oemReference?: string
  resolutionDate?: string
  resolutionNotes?: string
  clientSatisfaction?: 1 | 2 | 3 | 4 | 5
  createdAt: string
  updatedAt: string
}

interface Client {
  id: string
  name: string
  email: string
  phone: string
  company?: string
  industry?: string
}

interface Agent {
  id: string
  name: string
  email: string
  department: string
}

interface Engineer {
  id: string
  name: string
  email: string
  specialization: string
}

// LocalStorage keys
const FEEDBACK_STORAGE_KEY = 'client_feedback_data'
const CLIENTS_STORAGE_KEY = 'feedback_clients'
const AGENTS_STORAGE_KEY = 'feedback_agents'
const ENGINEERS_STORAGE_KEY = 'feedback_engineers'

// Mock data
const mockFeedback = ref<FeedbackItem[]>([
  {
    id: '1',
    srNo: 1,
    tenderId: 'TND-2024-001',
    tenderName: 'Government School Laptop Tender',
    clientId: 'C001',
    clientName: 'Rajesh Kumar',
    clientEmail: 'rajesh.kumar@school.edu.in',
    clientPhone: '+91 9876543210',
    itemType: 'laptop',
    itemDescription: 'Lenovo ThinkPad Laptop',
    itemBrand: 'Lenovo',
    itemModel: 'ThinkPad X1 Carbon',
    feedbackDate: '2024-03-15T10:30:00Z',
    feedbackType: 'complaint',
    feedbackPriority: 'high',
    feedbackMessage: 'Laptop battery drains too quickly. Lasts only 2 hours instead of promised 8 hours.',
    status: 'assigned_to_oem',
    assignedToAgentId: 'A001',
    assignedToAgentName: 'Priya Sharma',
    assignedToEngineerId: 'E001',
    assignedToEngineerName: 'Amit Patel',
    oemReference: 'OEM-REF-2024-001',
    createdAt: '2024-03-15T10:30:00Z',
    updatedAt: '2024-03-16T14:20:00Z'
  },
  {
    id: '2',
    srNo: 2,
    tenderId: 'TND-2024-002',
    tenderName: 'Hospital Printer Supply',
    clientId: 'C002',
    clientName: 'Dr. Meena Desai',
    clientEmail: 'meena.desai@cityhospital.in',
    clientPhone: '+91 9876543211',
    itemType: 'printer',
    itemDescription: 'HP LaserJet Printer',
    itemBrand: 'HP',
    itemModel: 'LaserJet Pro M404dn',
    feedbackDate: '2024-03-14T14:45:00Z',
    feedbackType: 'query',
    feedbackPriority: 'medium',
    feedbackMessage: 'Printer showing paper jam error frequently. Need technical assistance.',
    status: 'in_progress',
    assignedToAgentId: 'A002',
    assignedToAgentName: 'Rahul Verma',
    assignedToEngineerId: 'E002',
    assignedToEngineerName: 'Suresh Kumar',
    createdAt: '2024-03-14T14:45:00Z',
    updatedAt: '2024-03-15T11:30:00Z'
  },
  {
    id: '3',
    srNo: 3,
    tenderId: 'TND-2024-003',
    tenderName: 'Corporate Server Upgrade',
    clientId: 'C003',
    clientName: 'Tech Solutions Pvt Ltd',
    clientEmail: 'support@techsolutions.com',
    clientPhone: '+91 9876543212',
    itemType: 'server',
    itemDescription: 'Dell PowerEdge Server',
    itemBrand: 'Dell',
    itemModel: 'PowerEdge R740',
    feedbackDate: '2024-03-13T09:15:00Z',
    feedbackType: 'complaint',
    feedbackPriority: 'critical',
    feedbackMessage: 'Server overheating issue. System shuts down randomly during peak hours.',
    status: 'new',
    createdAt: '2024-03-13T09:15:00Z',
    updatedAt: '2024-03-13T09:15:00Z'
  },
  {
    id: '4',
    srNo: 4,
    tenderId: 'TND-2024-004',
    tenderName: 'University Network Setup',
    clientId: 'C004',
    clientName: 'Professor S. Gupta',
    clientEmail: 's.gupta@university.edu',
    clientPhone: '+91 9876543213',
    itemType: 'network',
    itemDescription: 'Cisco Network Switches',
    itemBrand: 'Cisco',
    itemModel: 'Catalyst 9300',
    feedbackDate: '2024-03-12T16:20:00Z',
    feedbackType: 'suggestion',
    feedbackPriority: 'low',
    feedbackMessage: 'Suggest adding more network ports in computer lab for future expansion.',
    status: 'resolved',
    assignedToAgentId: 'A001',
    assignedToAgentName: 'Priya Sharma',
    assignedToEngineerId: 'E003',
    assignedToEngineerName: 'Vikram Singh',
    resolutionDate: '2024-03-14T15:30:00Z',
    resolutionNotes: 'Additional ports installed. Client satisfied.',
    clientSatisfaction: 5,
    createdAt: '2024-03-12T16:20:00Z',
    updatedAt: '2024-03-14T15:30:00Z'
  },
  {
    id: '5',
    srNo: 5,
    tenderId: 'TND-2024-005',
    tenderName: 'Bank Software License',
    clientId: 'C005',
    clientName: 'State Bank IT Department',
    clientEmail: 'it@statebank.co.in',
    clientPhone: '+91 9876543214',
    itemType: 'software',
    itemDescription: 'Accounting Software Suite',
    itemBrand: 'SAP',
    itemModel: 'SAP S/4HANA',
    feedbackDate: '2024-03-11T11:45:00Z',
    feedbackType: 'appreciation',
    feedbackPriority: 'low',
    feedbackMessage: 'Excellent support team. Quick resolution of licensing issues.',
    status: 'closed',
    assignedToAgentId: 'A003',
    assignedToAgentName: 'Anjali Mehta',
    resolutionDate: '2024-03-12T10:15:00Z',
    resolutionNotes: 'Client appreciated the service. Case closed.',
    clientSatisfaction: 5,
    createdAt: '2024-03-11T11:45:00Z',
    updatedAt: '2024-03-12T10:15:00Z'
  },
  {
    id: '6',
    srNo: 6,
    tenderId: 'TND-2024-001',
    tenderName: 'Government School Laptop Tender',
    clientId: 'C001',
    clientName: 'Rajesh Kumar',
    clientEmail: 'rajesh.kumar@school.edu.in',
    clientPhone: '+91 9876543210',
    itemType: 'laptop',
    itemDescription: 'HP EliteBook Laptop',
    itemBrand: 'HP',
    itemModel: 'EliteBook 840 G8',
    feedbackDate: '2024-03-10T13:30:00Z',
    feedbackType: 'complaint',
    feedbackPriority: 'medium',
    feedbackMessage: 'Keyboard keys sticking. Needs replacement.',
    status: 'reopened',
    assignedToAgentId: 'A001',
    assignedToAgentName: 'Priya Sharma',
    assignedToEngineerId: 'E001',
    assignedToEngineerName: 'Amit Patel',
    resolutionDate: '2024-03-11T16:45:00Z',
    resolutionNotes: 'Keyboard replaced, but client reports same issue.',
    clientSatisfaction: 2,
    createdAt: '2024-03-10T13:30:00Z',
    updatedAt: '2024-03-12T09:20:00Z'
  }
])

const mockClients = ref<Client[]>([
  { id: 'C001', name: 'Rajesh Kumar', email: 'rajesh.kumar@school.edu.in', phone: '+91 9876543210', company: 'City Public School', industry: 'Education' },
  { id: 'C002', name: 'Dr. Meena Desai', email: 'meena.desai@cityhospital.in', phone: '+91 9876543211', company: 'City General Hospital', industry: 'Healthcare' },
  { id: 'C003', name: 'Tech Solutions Pvt Ltd', email: 'support@techsolutions.com', phone: '+91 9876543212', company: 'Tech Solutions Pvt Ltd', industry: 'IT Services' },
  { id: 'C004', name: 'Professor S. Gupta', email: 's.gupta@university.edu', phone: '+91 9876543213', company: 'State University', industry: 'Education' },
  { id: 'C005', name: 'State Bank IT Department', email: 'it@statebank.co.in', phone: '+91 9876543214', company: 'State Bank of India', industry: 'Banking' }
])

const mockAgents = ref<Agent[]>([
  { id: 'A001', name: 'Priya Sharma', email: 'priya.sharma@company.com', department: 'Client Relations' },
  { id: 'A002', name: 'Rahul Verma', email: 'rahul.verma@company.com', department: 'Sales' },
  { id: 'A003', name: 'Anjali Mehta', email: 'anjali.mehta@company.com', department: 'Support' }
])

const mockEngineers = ref<Engineer[]>([
  { id: 'E001', name: 'Amit Patel', email: 'amit.patel@company.com', specialization: 'Hardware' },
  { id: 'E002', name: 'Suresh Kumar', email: 'suresh.kumar@company.com', specialization: 'Printers & Scanners' },
  { id: 'E003', name: 'Vikram Singh', email: 'vikram.singh@company.com', specialization: 'Networking' },
  { id: 'E004', name: 'Neha Reddy', email: 'neha.reddy@company.com', specialization: 'Software' }
])

// Reactive state
const feedbackList = ref<FeedbackItem[]>([])
const filteredFeedback = ref<FeedbackItem[]>([])
const clients = ref<Client[]>([])
const agents = ref<Agent[]>([])
const engineers = ref<Engineer[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Filters
const searchFilter = ref('')
const statusFilter = ref('')
const itemTypeFilter = ref('')
const clientFilter = ref('')

// Modals
const showFeedbackModal = ref(false)
const showAssignModal = ref(false)
const showDeleteModal = ref(false)
const selectedFeedback = ref<FeedbackItem | null>(null)
const feedbackToDelete = ref<FeedbackItem | null>(null)

// Action selection
const selectedAction = ref('')

// Assignment form
const assignmentForm = ref({
  agentId: '',
  engineerId: '',
  oemReference: '',
  notes: ''
})

// Filter options
const statusOptions = [
  { label: 'All Status', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Assigned to OEM', value: 'assigned_to_oem' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
  { label: 'Reopened', value: 'reopened' }
]

// Action options (computed based on selected feedback)
const actionOptions = computed(() => {
  const options = [
    { label: 'Select Action', value: '' }
  ]

  if (selectedFeedback.value) {
    // Add Mark as Resolved option
    if (selectedFeedback.value.status !== 'resolved' &&
        selectedFeedback.value.status !== 'closed') {
      options.push({
        label: 'Mark as Resolved',
        value: 'resolve'
      })
    }

    // Add Close Feedback option
    if (selectedFeedback.value.status === 'resolved') {
      options.push({
        label: 'Close Feedback',
        value: 'close'
      })
    }

    // Add Reopen option
    if (selectedFeedback.value.status === 'resolved' ||
        selectedFeedback.value.status === 'closed') {
      options.push({
        label: 'Reopen Feedback',
        value: 'reopen'
      })
    }

    // Add Delete option (always available)
    options.push({
      label: 'Delete Feedback',
      value: 'delete'
    })
  }

  return options
})

const agentOptions = computed(() => {
  return [
    { label: 'Select Agent', value: '' },
    ...agents.value.map(agent => ({
      label: `${agent.name} - ${agent.department}`,
      value: agent.id
    }))
  ]
})

const engineerOptions = computed(() => {
  return [
    { label: 'Select Engineer (Optional)', value: '' },
    ...engineers.value.map(engineer => ({
      label: `${engineer.name} - ${engineer.specialization}`,
      value: engineer.id
    }))
  ]
})

// Filter feedback
const applyFilters = () => {
  let filtered = feedbackList.value

  // Apply status filter
  if (statusFilter.value) {
    filtered = filtered.filter(feedback => feedback.status === statusFilter.value)
  }

  // Apply item type filter
  if (itemTypeFilter.value) {
    filtered = filtered.filter(feedback => feedback.itemType === itemTypeFilter.value)
  }

  // Apply client filter
  if (clientFilter.value) {
    filtered = filtered.filter(feedback => feedback.clientId === clientFilter.value)
  }

  // Apply search filter
  if (searchFilter.value) {
    const searchTerm = searchFilter.value.toLowerCase()
    filtered = filtered.filter(feedback =>
      feedback.tenderId.toLowerCase().includes(searchTerm) ||
      feedback.tenderName.toLowerCase().includes(searchTerm) ||
      feedback.clientName.toLowerCase().includes(searchTerm) ||
      feedback.itemDescription.toLowerCase().includes(searchTerm) ||
      feedback.feedbackMessage.toLowerCase().includes(searchTerm)
    )
  }

  filteredFeedback.value = filtered
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  } catch {
    return dateString
  }
}

const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

// Get status badge class
const getStatusBadgeClass = (status: FeedbackItem['status']) => {
  const classMap = {
    new: 'bg-blue-100 text-blue-800 border border-blue-200',
    assigned_to_oem: 'bg-purple-100 text-purple-800 border border-purple-200',
    in_progress: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    resolved: 'bg-green-100 text-green-800 border border-green-200',
    closed: 'bg-gray-100 text-gray-800 border border-gray-200',
    reopened: 'bg-red-100 text-red-800 border border-red-200'
  }
  return classMap[status]
}

// Get status label
const getStatusLabel = (status: FeedbackItem['status']) => {
  const labelMap = {
    new: 'New',
    assigned_to_oem: 'Assigned to OEM',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    reopened: 'Reopened'
  }
  return labelMap[status]
}

// Get priority badge class
const getPriorityBadgeClass = (priority: FeedbackItem['feedbackPriority']) => {
  const classMap = {
    critical: 'bg-red-100 text-red-800 border border-red-200',
    high: 'bg-orange-100 text-orange-800 border border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    low: 'bg-green-100 text-green-800 border border-green-200'
  }
  return classMap[priority]
}

// Get priority label
const getPriorityLabel = (priority: FeedbackItem['feedbackPriority']) => {
  const labelMap = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low'
  }
  return labelMap[priority]
}

// Get item type icon
// const getItemTypeIcon = (itemType: FeedbackItem['itemType']) => {
//   const iconMap = {
//     laptop: '💻',
//     printer: '🖨️',
//     server: '🖥️',
//     network: '🌐',
//     software: '📱',
//     other: '📦'
//   }
//   return iconMap[itemType]
// }

// Open feedback detail modal
const openFeedbackModal = (feedback: FeedbackItem) => {
  selectedFeedback.value = feedback
  selectedAction.value = ''
  showFeedbackModal.value = true
}

// Handle action selection
const handleActionSelect = (actionValue: string) => {
  if (!selectedFeedback.value || !actionValue) return

  switch (actionValue) {
    case 'resolve':
      if (confirm('Are you sure you want to mark this feedback as resolved?')) {
        markAsResolved(selectedFeedback.value)
        selectedAction.value = ''
      }
      break

    case 'close':
      if (confirm('Are you sure you want to close this feedback?')) {
        markAsClosed(selectedFeedback.value)
        selectedAction.value = ''
      }
      break

    case 'reopen':
      if (confirm('Are you sure you want to reopen this feedback?')) {
        reopenFeedback(selectedFeedback.value)
        selectedAction.value = ''
      }
      break

    case 'delete':
      openDeleteModal(selectedFeedback.value)
      showFeedbackModal.value = false
      selectedAction.value = ''
      break
  }

  // Reset selection after handling
  setTimeout(() => {
    selectedAction.value = ''
  }, 100)
}

// Open assign modal
const openAssignModal = (feedback: FeedbackItem) => {
  selectedFeedback.value = feedback
  assignmentForm.value = {
    agentId: feedback.assignedToAgentId || '',
    engineerId: feedback.assignedToEngineerId || '',
    oemReference: feedback.oemReference || '',
    notes: ''
  }
  showAssignModal.value = true
}

// Handle assignment
const handleAssign = () => {
  if (!selectedFeedback.value) return

  if (!assignmentForm.value.agentId) {
    alert('Please select an agent')
    return
  }

  const agent = agents.value.find(a => a.id === assignmentForm.value.agentId)
  const engineer = engineers.value.find(e => e.id === assignmentForm.value.engineerId)

  const index = feedbackList.value.findIndex(f => f.id === selectedFeedback.value!.id)
  if (index !== -1) {
    feedbackList.value[index] = {
      ...feedbackList.value[index],
      status: assignmentForm.value.oemReference ? 'assigned_to_oem' : 'in_progress',
      assignedToAgentId: assignmentForm.value.agentId,
      assignedToAgentName: agent?.name || '',
      assignedToEngineerId: assignmentForm.value.engineerId || undefined,
      assignedToEngineerName: engineer?.name || undefined,
      oemReference: assignmentForm.value.oemReference || undefined,
      updatedAt: new Date().toISOString()
    }

    // Save to localStorage
    saveToLocalStorage()

    // Apply filters after update
    applyFilters()

    alert('✅ Feedback assigned successfully!')

    // Close modal
    showAssignModal.value = false
    assignmentForm.value = {
      agentId: '',
      engineerId: '',
      oemReference: '',
      notes: ''
    }
  }
}

// Mark as resolved
const markAsResolved = (feedback: FeedbackItem) => {
  const index = feedbackList.value.findIndex(f => f.id === feedback.id)
  if (index !== -1) {
    feedbackList.value[index] = {
      ...feedbackList.value[index],
      status: 'resolved',
      resolutionDate: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    saveToLocalStorage()
    applyFilters()
    alert('✅ Feedback marked as resolved!')
  }
}

// Mark as closed
const markAsClosed = (feedback: FeedbackItem) => {
  const index = feedbackList.value.findIndex(f => f.id === feedback.id)
  if (index !== -1) {
    feedbackList.value[index] = {
      ...feedbackList.value[index],
      status: 'closed',
      updatedAt: new Date().toISOString()
    }

    saveToLocalStorage()
    applyFilters()
    alert('✅ Feedback closed!')
  }
}

// Reopen feedback
const reopenFeedback = (feedback: FeedbackItem) => {
  const index = feedbackList.value.findIndex(f => f.id === feedback.id)
  if (index !== -1) {
    feedbackList.value[index] = {
      ...feedbackList.value[index],
      status: 'reopened',
      updatedAt: new Date().toISOString()
    }

    saveToLocalStorage()
    applyFilters()
    alert('✅ Feedback reopened!')
  }
}

// Open delete confirmation modal
const openDeleteModal = (feedback: FeedbackItem) => {
  feedbackToDelete.value = feedback
  showDeleteModal.value = true
}

// Handle delete
const handleDeleteFeedback = () => {
  if (!feedbackToDelete.value) return

  const index = feedbackList.value.findIndex(f => f.id === feedbackToDelete.value!.id)
  if (index !== -1) {
    feedbackList.value.splice(index, 1)

    saveToLocalStorage()
    applyFilters()
    alert('✅ Feedback deleted successfully!')
  }

  showDeleteModal.value = false
  feedbackToDelete.value = null
}

// Save to localStorage
const saveToLocalStorage = () => {
  try {
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedbackList.value))
    localStorage.setItem(CLIENTS_STORAGE_KEY, JSON.stringify(clients.value))
    localStorage.setItem(AGENTS_STORAGE_KEY, JSON.stringify(agents.value))
    localStorage.setItem(ENGINEERS_STORAGE_KEY, JSON.stringify(engineers.value))
  } catch (err) {
    console.error('Error saving to localStorage:', err)
  }
}

// Load from localStorage
const loadFromLocalStorage = () => {
  try {
    const storedFeedback = localStorage.getItem(FEEDBACK_STORAGE_KEY)
    if (storedFeedback) {
      feedbackList.value = JSON.parse(storedFeedback)
    }

    const storedClients = localStorage.getItem(CLIENTS_STORAGE_KEY)
    if (storedClients) {
      clients.value = JSON.parse(storedClients)
    }

    const storedAgents = localStorage.getItem(AGENTS_STORAGE_KEY)
    if (storedAgents) {
      agents.value = JSON.parse(storedAgents)
    }

    const storedEngineers = localStorage.getItem(ENGINEERS_STORAGE_KEY)
    if (storedEngineers) {
      engineers.value = JSON.parse(storedEngineers)
    }
  } catch (err) {
    console.error('Error loading from localStorage:', err)
    feedbackList.value = []
    clients.value = []
    agents.value = []
    engineers.value = []
  }
}

// Initialize with sample data if empty
const initializeWithSampleData = () => {
  if (feedbackList.value.length === 0) {
    feedbackList.value = [...mockFeedback.value]
  }
  if (clients.value.length === 0) {
    clients.value = [...mockClients.value]
  }
  if (agents.value.length === 0) {
    agents.value = [...mockAgents.value]
  }
  if (engineers.value.length === 0) {
    engineers.value = [...mockEngineers.value]
  }

  saveToLocalStorage()
}

// Fetch data
const fetchFeedbackData = () => {
  try {
    loading.value = true
    error.value = null

    loadFromLocalStorage()

    // If no data in localStorage, initialize with sample data
    if (feedbackList.value.length === 0 || clients.value.length === 0) {
      initializeWithSampleData()
    }

    applyFilters()

  } catch (err) {
    console.error('Error loading feedback data:', err)
    error.value = 'Failed to load feedback data.'
  } finally {
    loading.value = false
  }
}

// Refresh data
const refreshData = () => {
  fetchFeedbackData()
  alert('🔄 Feedback data refreshed!')
}

// Initialize
onMounted(() => {
  fetchFeedbackData()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Client Feedback Management" />

    <!-- Feedback Management Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Client Feedback"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex flex-wrap gap-2">
            <SelectOptionPrime
              v-model="statusFilter"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter by Status"
              id="status-filter"
              size="sm"
              class="w-40"
              @update:modelValue="applyFilters"
            />

            <TextInputDefault
              v-model="searchFilter"
              placeholder="Search feedback..."
              id="searchfeedback"
              name="searchfeedback"
              type="text"
              className="w-48"
              size="sm"
              @update:modelValue="applyFilters"
            />
          </div>

          <div class="flex gap-2">
            <BaseButton
              text="Clear Filters"
              size="sm"
              variant="ghost"
              @click="searchFilter = ''; statusFilter = ''; itemTypeFilter = ''; clientFilter = ''; applyFilters()"
              :disabled="!searchFilter && !statusFilter && !itemTypeFilter && !clientFilter"
            />
            <BaseButton
              :icon="RefreshIcon"
              size="sm"
              @click="refreshData"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"></i>
          Loading feedback data...
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="w-full flex flex-col items-center text-center py-8 gap-3 text-red-500">
          <p>{{ error }}</p>
          <BaseButton text="Retry" size="sm" @click="fetchFeedbackData" class="w-30" />
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="filteredFeedback.length === 0"
          :icon="CommentIcon"
          :title="searchFilter || statusFilter || itemTypeFilter ? 'No feedback matches your filter criteria' : 'No feedback found'"
          description="No client feedback available yet."
        />

        <!-- Feedback Table -->
        <template v-else>
          <DataTable
            :value="filteredFeedback"
            class="w-full"
            sortMode="multiple"
            scrollable
            scrollHeight="600px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            :pt="{
              table: { style: 'border-collapse: separate; border-spacing: 0;' },
              thead: { style: 'position: sticky; top: 0; z-index: 20;' },
              tbody: { style: 'position: relative; z-index: 10;' }
            }"
          >
            <!-- Serial Number Column -->
            <Column sortable field="srNo" header="Sr. No." :style="{ width: '80px' }">
              <template #body="{ data }">
                <div class="text-center">
                  {{ data.srNo }}
                </div>
              </template>
            </Column>

            <!-- Tender ID Column -->
            <Column sortable field="tenderId" header="Tender ID">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span class="font-medium">{{ data.tenderId }}</span>
                  <span class="text-xs text-gray-500 mt-1">{{ data.tenderName }}</span>
                </div>
              </template>
            </Column>

            <!-- Client Name Column -->
            <Column sortable field="clientName" header="Client Name">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span class="font-medium">{{ data.clientName }}</span>
                  <span class="text-xs text-gray-500 mt-1">{{ data.clientEmail }}</span>
                </div>
              </template>
            </Column>

            <!-- Feedback Date Column -->
            <Column sortable field="feedbackDate" header="Feedback Date">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span class="text-sm">{{ formatDate(data.feedbackDate) }}</span>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column sortable field="status" header="Status">
              <template #body="{ data }">
                <Badge
                  :value="getStatusLabel(data.status)"
                  :class="getStatusBadgeClass(data.status)"
                  class="px-3 py-1 rounded-full text-xs font-medium"
                />
              </template>
            </Column>

            <!-- Actions Column -->
            <Column frozen field="actions" header="Actions" :style="{ width: '120px' }">
              <template #body="{ data }">
                <div class="flex flex-wrap gap-1">
                  <!-- View Details -->
                  <BaseButton
                    size="xs"
                    :aria-label="`view feedback ${data.srNo}`"
                    @click="openFeedbackModal(data)"
                    variant="primary"
                    tooltip="View Details"
                    tooltipPosition="bottom"
                    :icon="EyeIcon"
                  />

                  <!-- Edit/Assign -->
                  <BaseButton
                    size="xs"
                    :aria-label="`edit feedback ${data.srNo}`"
                    @click="openAssignModal(data)"
                    variant="secondary"
                    tooltip="Edit/Assign"
                    tooltipPosition="bottom"
                    :icon="EditIcon"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </template>

      <template #footer v-if="!loading && !error && filteredFeedback.length > 0">
        <div class="text-xs text-gray-500">
          Showing {{ filteredFeedback.length }} of {{ feedbackList.length }} feedback entries
          <span class="ml-2">• Storage: localStorage</span>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <!-- Feedback Detail Modal -->
  <ModalDefault
    v-model="showFeedbackModal"
    :title="`Feedback Details - SR No: ${selectedFeedback?.srNo}`"
    width="max-w-3xl"
    classBody="my-4"
    @close="showFeedbackModal = false; selectedAction = ''"
  >
    <div v-if="selectedFeedback" class="space-y-6">
      <!-- Action Selector -->
      <div class="flex justify-end mb-4">
        <div class="w-48">
          <SelectOptionPrime
            v-model="selectedAction"
            :options="actionOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Action"
            id="feedback-action"
            size="sm"
            @update:modelValue="handleActionSelect"
          />
        </div>
      </div>

      <!-- Basic Information -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Tender Information -->
        <div class="space-y-3">
          <h3 class="text-lg font-medium text-gray-900">Tender Information</h3>
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600">Tender ID:</span>
              <span class="font-medium">{{ selectedFeedback.tenderId }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Tender Name:</span>
              <span class="font-medium">{{ selectedFeedback.tenderName }}</span>
            </div>
          </div>
        </div>

        <!-- Client Information -->
        <div class="space-y-3">
          <h3 class="text-lg font-medium text-gray-900">Client Information</h3>
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600">Client Name:</span>
              <span class="font-medium">{{ selectedFeedback.clientName }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Email:</span>
              <span class="font-medium">{{ selectedFeedback.clientEmail }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Phone:</span>
              <span class="font-medium">{{ selectedFeedback.clientPhone }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Item Information -->
      <div class="space-y-3">
        <h3 class="text-lg font-medium text-gray-900">Item Information</h3>
        <div class="bg-gray-50 p-4 rounded-lg">
          <div class="flex items-center gap-4">
            <div class="flex-1">
              <h4 class="font-medium text-lg">{{ selectedFeedback.itemDescription }}</h4>
              <div class="flex gap-3 mt-2">
                <div v-if="selectedFeedback.itemBrand" class="flex items-center gap-1">
                  <span class="text-gray-600 text-sm">Brand:</span>
                  <span class="font-medium">{{ selectedFeedback.itemBrand }}</span>
                </div>
                <div v-if="selectedFeedback.itemModel" class="flex items-center gap-1">
                  <span class="text-gray-600 text-sm">Model:</span>
                  <span class="font-medium">{{ selectedFeedback.itemModel }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feedback Details -->
      <div class="space-y-3">
        <h3 class="text-lg font-medium text-gray-900">Feedback Details</h3>
        <div class="bg-gray-50 p-4 rounded-lg">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-3">
              <Badge
                :value="getStatusLabel(selectedFeedback.status)"
                :class="getStatusBadgeClass(selectedFeedback.status)"
                class="px-3 py-1 rounded-full text-sm font-medium"
              />
              <Badge
                :value="getPriorityLabel(selectedFeedback.feedbackPriority)"
                :class="getPriorityBadgeClass(selectedFeedback.feedbackPriority)"
                class="px-3 py-1 rounded-full text-sm font-medium"
              />
            </div>
            <span class="text-gray-600 text-sm">
              {{ formatDateTime(selectedFeedback.feedbackDate) }}
            </span>
          </div>

          <div class="mt-4">
            <p class="text-gray-700">{{ selectedFeedback.feedbackMessage }}</p>
          </div>

          <!-- Resolution Details -->
          <div v-if="selectedFeedback.resolutionDate" class="mt-6 pt-4 border-t border-gray-200">
            <h4 class="font-medium text-gray-900 mb-2">Resolution Details</h4>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Resolved On:</span>
                <span class="font-medium">{{ formatDate(selectedFeedback.resolutionDate) }}</span>
              </div>
              <div v-if="selectedFeedback.resolutionNotes" class="mt-2">
                <span class="text-gray-600">Notes:</span>
                <p class="mt-1 text-gray-700">{{ selectedFeedback.resolutionNotes }}</p>
              </div>
              <div v-if="selectedFeedback.clientSatisfaction" class="mt-2">
                <span class="text-gray-600">Client Satisfaction:</span>
                <div class="flex items-center gap-1 mt-1">
                  <span v-for="i in 5" :key="i" class="text-xl">
                    {{ i <= selectedFeedback.clientSatisfaction ? '⭐' : '☆' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assignment Details -->
      <div class="space-y-3">
        <h3 class="text-lg font-medium text-gray-900">Assignment Details</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Agent -->
          <div class="bg-blue-50 p-4 rounded-lg">
            <h4 class="font-medium text-blue-900 mb-2">Assigned Agent</h4>
            <div v-if="selectedFeedback.assignedToAgentName" class="space-y-1">
              <p class="font-medium">{{ selectedFeedback.assignedToAgentName }}</p>
              <p class="text-sm text-blue-700">{{ selectedFeedback.assignedToAgentId }}</p>
            </div>
            <p v-else class="text-blue-600 italic">Not assigned</p>
          </div>

          <!-- Engineer -->
          <div class="bg-green-50 p-4 rounded-lg">
            <h4 class="font-medium text-green-900 mb-2">Assigned Engineer</h4>
            <div v-if="selectedFeedback.assignedToEngineerName" class="space-y-1">
              <p class="font-medium">{{ selectedFeedback.assignedToEngineerName }}</p>
              <p class="text-sm text-green-700">{{ selectedFeedback.assignedToEngineerId }}</p>
            </div>
            <p v-else class="text-green-600 italic">Not assigned</p>
          </div>
        </div>

        <!-- OEM Reference -->
        <div v-if="selectedFeedback.oemReference" class="bg-purple-50 p-4 rounded-lg">
          <h4 class="font-medium text-purple-900 mb-2">OEM Reference</h4>
          <p class="font-medium">{{ selectedFeedback.oemReference }}</p>
        </div>
      </div>

      <!-- Timeline -->
      <div class="space-y-3">
        <h3 class="text-lg font-medium text-gray-900">Timeline</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">Created:</span>
            <span class="font-medium">{{ formatDateTime(selectedFeedback.createdAt) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Last Updated:</span>
            <span class="font-medium">{{ formatDateTime(selectedFeedback.updatedAt) }}</span>
          </div>
        </div>
      </div>
    </div>
  </ModalDefault>

  <!-- Assign Feedback Modal -->
  <ModalDefault
    v-model="showAssignModal"
    :title="`Assign Feedback - SR No: ${selectedFeedback?.srNo}`"
    width="max-w-md"
    classBody="my-4"
    @close="showAssignModal = false"
  >
    <div v-if="selectedFeedback" class="space-y-4">
      <!-- Current Status -->
      <div class="bg-gray-50 p-3 rounded-lg">
        <div class="flex items-center gap-2">
          <span class="text-gray-600">Current Status:</span>
          <Badge
            :value="getStatusLabel(selectedFeedback.status)"
            :class="getStatusBadgeClass(selectedFeedback.status)"
            class="px-2 py-0.5 rounded-full text-xs"
          />
        </div>
        <p class="text-sm text-gray-500 mt-1">{{ selectedFeedback.tenderId }} - {{ selectedFeedback.itemDescription }}</p>
      </div>

      <!-- Assignment Form -->
      <div class="space-y-4">
        <!-- Agent Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">
            Assign to Agent <span class="text-red-500">*</span>
          </label>
          <SelectOptionPrime
            v-model="assignmentForm.agentId"
            :options="agentOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Agent"
            id="assign-agent"
            size="sm"
            required
          />
        </div>

        <!-- Engineer Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">
            Assign to Engineer (Optional)
          </label>
          <SelectOptionPrime
            v-model="assignmentForm.engineerId"
            :options="engineerOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Engineer"
            id="assign-engineer"
            size="sm"
          />
        </div>

        <!-- OEM Reference -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">
            OEM Reference (Optional)
          </label>
          <TextInputDefault
            v-model="assignmentForm.oemReference"
            type="text"
            id="oem-reference"
            name="oemReference"
            placeholder="Enter OEM reference number"
            size="sm"
            note="If feedback needs OEM involvement"
          />
        </div>

        <!-- Notes -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">
            Assignment Notes
          </label>
          <textarea
            v-model="assignmentForm.notes"
            rows="3"
            class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
            placeholder="Add any notes for the assignment..."
          ></textarea>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="showAssignModal = false"
        />
        <BaseButton
          text="Assign"
          variant="primary"
          @click="handleAssign"
          :disabled="!assignmentForm.agentId"
        />
      </div>
    </div>
  </ModalDefault>

  <!-- Delete Confirmation Modal -->
  <ModalDefault
    v-model="showDeleteModal"
    :title="`Delete Feedback - SR No: ${feedbackToDelete?.srNo}`"
    width="max-w-md"
    classBody="my-4"
    @close="showDeleteModal = false"
  >
    <div class="space-y-4">
      <div class="bg-red-50 border-l-4 border-red-400 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            ⚠️
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">
              <span class="font-medium">Warning:</span>
              Are you sure you want to delete this feedback entry? This action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      <div v-if="feedbackToDelete" class="bg-gray-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-gray-700 mb-2">Feedback Details:</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">Tender ID:</span>
            <span class="font-medium">{{ feedbackToDelete.tenderId }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Client:</span>
            <span class="font-medium">{{ feedbackToDelete.clientName }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Item:</span>
            <span class="font-medium">{{ feedbackToDelete.itemDescription }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Status:</span>
            <Badge
              :value="getStatusLabel(feedbackToDelete.status)"
              :class="getStatusBadgeClass(feedbackToDelete.status)"
              class="px-2 py-0.5 text-xs"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="showDeleteModal = false"
        />
        <BaseButton
          text="Delete Feedback"
          variant="danger"
          @click="handleDeleteFeedback"
        />
      </div>
    </div>
  </ModalDefault>
</template>
<!--
<style scoped>
.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style> -->
